'use client'

import { useCallback } from 'react'
import {
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query'

import { useClientApi } from '../useClientApi'

import type {
  TGetLineupsOutput,
  TGetLineupByIdOutput,
  TCreateLineupInput,
  TCreateLineupOutput,
  TUpdateLineupInput,
  TUpdateLineupOutput,
  TDeleteLineupInput,
  TDeleteLineupOutput,
  TShareLineupInput,
  TShareLineupOutput,
  TLineUp
} from '@/api/v1/resources/lineup'

// Re-export types for convenience
export type { TLineUp, TGetLineupsOutput }

export type TOptionsQueryLineups = UseQueryOptions<
  TGetLineupsOutput,
  unknown,
  TGetLineupsOutput,
  QueryKey
>

export type TOptionsQueryLineupById = UseQueryOptions<
  TGetLineupByIdOutput,
  unknown,
  TGetLineupByIdOutput,
  QueryKey
>

/**
 * Get query key for all lineups
 */
export const getLineupsKey = (): QueryKey => ['v1', 'lineup']

/**
 * Get query key for a specific lineup by ID
 */
export const getLineupKey = (id?: string): QueryKey => ['v1', 'lineup', { id }]

/**
 * Query hook for fetching all user lineups (owned and invited)
 */
export const useQueryLineups = (options?: TOptionsQueryLineups) => {
  const { api, userInitialized } = useClientApi()
  return useQuery({
    queryKey: getLineupsKey(),
    queryFn: async () => api.lineup.getAll(),
    enabled: !!userInitialized,
    ...options
  })
}

/**
 * Query hook for fetching a specific lineup by ID
 */
export const useQueryLineupById = (id: string | undefined, options?: TOptionsQueryLineupById) => {
  const { api, userInitialized } = useClientApi()
  const { enabled: optionsEnabled, ...restOptions } = options ?? {}

  const baseEnabled = optionsEnabled ?? !!id
  const enabled = !!userInitialized && baseEnabled

  return useQuery({
    queryKey: getLineupKey(id),
    queryFn: async () => {
      if (!id) throw new Error('Lineup ID is required')
      return api.lineup.getById({ id })
    },
    enabled,
    ...restOptions
  })
}

/**
 * Mutation hook for creating a new lineup
 */
export const useMutationCreateLineup = (
  options?: UseMutationOptions<TCreateLineupOutput, unknown, TCreateLineupInput>
) => {
  const { api } = useClientApi()
  return useMutation({
    mutationFn: async (input: TCreateLineupInput) => api.lineup.create(input),
    ...options
  })
}

/**
 * Mutation hook for updating an existing lineup
 */
export const useMutationUpdateLineup = (
  options?: UseMutationOptions<TUpdateLineupOutput, unknown, TUpdateLineupInput>
) => {
  const { api } = useClientApi()
  return useMutation({
    mutationFn: async (input: TUpdateLineupInput) => api.lineup.update(input),
    ...options
  })
}

/**
 * Mutation hook for deleting a lineup
 */
export const useMutationDeleteLineup = (
  options?: UseMutationOptions<TDeleteLineupOutput, unknown, TDeleteLineupInput>
) => {
  const { api } = useClientApi()
  return useMutation({
    mutationFn: async (input: TDeleteLineupInput) => api.lineup.delete(input),
    ...options
  })
}

/**
 * Mutation hook for updating lineup sharing recipients
 */
export const useMutationShareLineup = (
  options?: UseMutationOptions<TShareLineupOutput, unknown, TShareLineupInput>
) => {
  const { api } = useClientApi()
  return useMutation({
    mutationFn: async (input: TShareLineupInput) => api.lineup.shareWith(input),
    ...options
  })
}

/**
 * Main hook for lineup management.
 * Combines all query and mutation functionality for lineup operations.
 *
 * @param lineupId - Optional specific lineup ID to fetch
 * @param options - Optional query options for the lineups query
 * @returns Object containing all lineup data, mutations, and utility functions
 *
 * @example
 * ```typescript
 * const { lineups, create, update, delete: deleteLineup, shareWith, isLoading } = useLineup()
 *
 * // Create a new lineup
 * create.mutate({ name: 'My Lineup', vessels: [{ imo: '1234567' }] })
 *
 * // Update lineup sharing
 * shareWith.mutate({ id: lineupId, recipients: [{ recipient: 'test@example.com', medium: 'EMAIL' }] })
 * ```
 */
export default function useLineup(options?: TOptionsQueryLineups) {
  const queryClient = useQueryClient()

  // Query hooks
  const lineupsQuery = useQueryLineups(options)

  /**
   * Invalidate all lineups cache
   */
  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: getLineupsKey() })
  }, [queryClient])

  /**
   * Invalidate a specific lineup cache
   */
  const invalidateById = useCallback(
    (id: string) => {
      queryClient.invalidateQueries({ queryKey: getLineupKey(id) })
    },
    [queryClient]
  )

  /**
   * Invalidate all lineup caches (list and specific)
   */
  const invalidateAll = useCallback(() => {
    invalidate()
  }, [invalidate])

  // Mutation hooks with automatic cache invalidation
  const createMutation = useMutationCreateLineup({
    onSuccess: () => invalidate()
  })

  const updateMutation = useMutationUpdateLineup({
    onSuccess: (_data, variables) => {
      // Update the specific lineup in cache
      queryClient.setQueryData(getLineupKey(variables.id), _data)
      // Invalidate the list to ensure consistency
      invalidate()
    }
  })

  const deleteMutation = useMutationDeleteLineup({
    onSuccess: (_data, variables) => {
      invalidate()
      invalidateById(variables.id)
    }
  })

  const shareMutation = useMutationShareLineup({
    onSuccess: (_data, variables) => {
      invalidate()
      invalidateById(variables.id)
    }
  })

  // Convenience method to get a specific lineup from the cached list
  const getLineupFromList = useCallback(
    (id: string): TLineUp | undefined => {
      const owned = lineupsQuery.data?.owned.find((l) => l._id === id)
      if (owned) return owned
      return lineupsQuery.data?.guest.find((l) => l._id === id)
    },
    [lineupsQuery.data]
  )

  return {
    // Query data and states
    ...lineupsQuery,
    lineups: lineupsQuery.data,
    ownedLineups: lineupsQuery.data?.owned ?? [],
    invitedLineups: lineupsQuery.data?.guest ?? [],

    // Loading states
    isLoading: lineupsQuery.isLoading,
    isFetching: lineupsQuery.isFetching,

    // Error states
    error: lineupsQuery.error,

    // Cache invalidation
    invalidate,
    invalidateById,
    invalidateAll,

    // Mutations
    create: createMutation,
    update: updateMutation,
    delete: deleteMutation,
    shareWith: shareMutation,

    // Mutation states
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isSharing: shareMutation.isPending,

    // Mutation errors
    createError: createMutation.error,
    updateError: updateMutation.error,
    deleteError: deleteMutation.error,
    shareError: shareMutation.error,

    // Convenience methods
    getLineupFromList
  }
}
